// web/src/features/favorites/useFavoritesStore.ts
import { create } from 'zustand';
import { api } from '@/shared/api/client';
import type { FavoriteRow, FavoriteContext } from './resolveFavoriteContext';
import { favoriteKey } from './resolveFavoriteContext';

type State = {
  favorites: FavoriteRow[];
  hydrated: boolean;
  hydrate: () => Promise<void>;
  isFavorited: (key: string) => boolean;
  add: (ctx: FavoriteContext) => Promise<void>;
  remove: (id: string) => Promise<void>;
  reorder: (ids: string[]) => Promise<void>;
  reset: () => void;
};

export const useFavoritesStore = create<State>((set, get) => ({
  favorites: [],
  hydrated: false,
  hydrate: async () => {
    try {
      const data = await api.get<FavoriteRow[]>('/favorites');
      set({ favorites: data, hydrated: true });
    } catch { set({ hydrated: true }); }
  },
  isFavorited: (key) => get().favorites.some((f) => favoriteKey(f) === key),
  add: async (ctx) => {
    const row = await api.post<FavoriteRow>('/favorites', ctx);
    set((s) => (s.favorites.some((f) => f.id === row.id) ? s : { favorites: [...s.favorites, row] }));
  },
  remove: async (id) => {
    const prev = get().favorites;
    set({ favorites: prev.filter((f) => f.id !== id) }); // optimistic
    try { await api.del(`/favorites/${id}`); } catch { set({ favorites: prev }); }
  },
  reorder: async (ids) => {
    const byId = new Map(get().favorites.map((f) => [f.id, f]));
    set({ favorites: ids.map((id) => byId.get(id)).filter(Boolean) as FavoriteRow[] });
    try { await api.patch('/favorites/reorder', { ids }); } catch { /* next hydrate corrects */ }
  },
  reset: () => set({ favorites: [], hydrated: false }),
}));
